import { AccountData, ApiUserRefresh, AppThunk, ManagedUserProfile, UserProfile, ApiUserRegister, ApiUpdateProfile, SignUpUserProps } from 'types';
import * as actions from 'store/actions';
import _get from 'lodash/get';
import { apiPost } from './shared/api';

export const registerUser = (data: SignUpUserProps): AppThunk<Promise<ApiUserRegister | null>> => {
  return (dispatch) => {
    return dispatch(apiPost<ApiUserRegister, SignUpUserProps>('v2_userRegister', data, true));
  };
};

export const signUpUser = (data: SignUpUserProps): AppThunk<Promise<void>> => {
  return async (dispatch) => {
    dispatch(actions.signUpStart());
    try {
      const response = await dispatch(registerUser(data));
      if (response) {
        const UserId = _get(response, 'payload.UserId', '') as string;
        if (UserId) {
          localStorage.setItem('signedInAs', JSON.stringify({ UserId }));
          localStorage.setItem('postingAs', JSON.stringify({ UserId }));
          dispatch(actions.signUpSuccess(UserId, _get(response, 'payload.CodeSentTo')));
          return;
        }
      }
      dispatch(actions.signUpFailed('Something went wrong'));
    } catch (error) {
      dispatch(actions.signUpFailed(_get(error, 'response.error_message')));
    }
  };
};

export const fetchUserProfile = (
  isManagedUser: boolean,
  UserId?: string,
  userStatus?: string,
  postingAsSameAsSignedInAs?: boolean,
): AppThunk<Promise<void>> => {
  return async (dispatch, getState) => {
    const { signedInAs } = getState().user;
    if (getState().user.loading) return;
    dispatch(actions.fetchProfileStart());
    try {
      const data: { UserId?: string } = {};
      if (isManagedUser && signedInAs.UserId !== UserId) {
        data.UserId = UserId;
      }
      const response = await dispatch(apiPost<ApiUserRefresh, { UserId?: string }>('v2_userRefresh', data));
      if (response?.success === true) {
        const user = response.payload.User.User;
        const logs = response.payload.Logs?.Logs || [];
        const skinTones = JSON.parse(localStorage.getItem('skinTones') || '{}') as Record<string, number>;
        const emojiSkinTone = user.UserId ? skinTones : null;
        const groups = response.payload.Groups?.Groups || [];
        const managedUsers = response.payload.ManagedUsers?.ManagedUsers || [];
        const knownUsers = response.payload.KnownUsers;
        const giftsDraftsCount = response.payload.DraftSummary
          ? response.payload.DraftSummary.DraftCount + response.payload.DraftSummary.DraftGiftCount
          : 0;
        if (user.FirstName !== '') {
          dispatch(
            actions.fetchProfileSuccess(
              user,
              logs,
              managedUsers,
              groups,
              isManagedUser,
              userStatus,
              giftsDraftsCount,
              knownUsers,
              postingAsSameAsSignedInAs,
              emojiSkinTone,
            ),
          );
        } else {
          dispatch(actions.fetchProfileReturnsNewUser(user.UserId));
        }
      } else {
        dispatch(actions.fetchProfileFailed(_get(response, 'error_message')));
      }
    } catch (error) {
      dispatch(actions.fetchProfileFailed(_get(error, 'response.error_message')));
    }
  };
};

export const switchUser = (userId: string): AppThunk => {
  return async (dispatch, getState) => {
    localStorage.removeItem('logFilters');
    localStorage.removeItem('logSortBy');
    localStorage.removeItem('postViewSize');

    dispatch(actions.clearAllLogs());

    const { postingAs, signedInAs } = getState().user;
    const userPostingAs = postingAs?.FirstName ? postingAs : signedInAs;

    if (userPostingAs.UserId !== userId) {
      if (signedInAs.UserId === userId) {
        dispatch(actions.revertToSignedInAs());
      } else {
        dispatch(actions.updatePostingAs(userId));
      }
      await dispatch(fetchUserProfile(true, userId, 'postingAs'));
    }
  };
};

export const getUserProfileDetails = (postingAsUser: ApiUserRefresh['payload'], userId: string): ManagedUserProfile | undefined => {
  const userObject = userId && postingAsUser?.KnownUsers ? postingAsUser.KnownUsers.find((user) => user.User.UserId === userId) : undefined;
  return userObject?.User;
};

export const userDetails = (postingAsUser: ApiUserRefresh['payload'], userId: string): AppThunk<ManagedUserProfile | Record<string, unknown>> => {
  return (dispatch) => {
    const userObject = getUserProfileDetails(postingAsUser, userId);
    if (!userObject) {
      // eslint-disable-next-line no-void
      void dispatch(fetchUserProfile(false, userId, 'postingAs', _get(postingAsUser, 'UserId') === userId));
    }
    return userObject || {};
  };
};

export const getAccountData = (action: AccountData, newProfile?: boolean): AccountData => {
  const data: AccountData = {};
  if (action.Color) data.Color = action.Color;
  if (action.FirstName) data.FirstName = action.FirstName;
  if (action.LastName) data.LastName = action.LastName;
  // eslint-disable-next-line prefer-destructuring
  if (action.DateBirthDay) data.DateBirthDay = action.DateBirthDay.split(' ')[0];
  if (action.NickName) data.NickName = action.NickName;
  if (action.profileImageId) data.ImageId = action.profileImageId;
  if (action.Phone) data.Phone = action.Phone;
  if (action.isManagedUser || newProfile) {
    data.UserId = action.UserId;
  }
  return data;
};

export const updateProfile = (data: AccountData, managedProfile?: boolean): AppThunk<Promise<ApiUpdateProfile | null>> => {
  return (dispatch) => {
    return dispatch(apiPost<ApiUpdateProfile, AccountData>('v2_userProfileUpdate', getAccountData(data, managedProfile)));
  };
};

export const submitProfile = (data: AccountData): AppThunk<Promise<void>> => {
  return async (dispatch) => {
    dispatch(actions.submitProfileStart());
    try {
      const response = await dispatch(updateProfile(data));
      const userProfileData = _get(response, 'payload.User') as UserProfile;
      const logs = _get(response, 'payload.Timelines') as unknown;
      const profileStatus = _get(response, 'payload.ProfileStatus') as string;
      dispatch(actions.submitProfileSuccess(userProfileData, logs, profileStatus));
      // eslint-disable-next-line no-void
      await dispatch(fetchUserProfile(false, userProfileData.UserId, 'signedInAs', true));
      // const userStatus = action.isManagedUser ? 'postingAs' : 'signedInAs';
      // const postingAsSameAsSignedInAs = action.isManagedUser === true ? false : true;
      // yield put(actions.fetchUserProfile(userProfileData.UserId, action.isManagedUser, userStatus, postingAsSameAsSignedInAs));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('submitProfileSaga: error', error);
      dispatch(actions.submitProfileFailed(_get(error, 'response.error_message')));
    }
  };
};

const createManagedUser = (): AppThunk<Promise<ApiUserRegister | null>> => {
  return (dispatch) => {
    return dispatch(apiPost<ApiUserRegister>('v2_userManagedCreate', {}));
  };
};

export const createManagedAccount = (data: AccountData): AppThunk<Promise<void>> => {
  return async (dispatch, getState) => {
    dispatch(actions.createManagedAccountStart());
    try {
      // const response = await dispatch(apiPost('v2_userManagedCreate', {}));
      // const { FirstName, LastName, Phone } = data;
      // const ManagedById = getState().user.signedInAs.UserId;
      // const response = await dispatch(registerUser({ FirstName, LastName, Phone, ManagedById, Email: '', Pass: '' }));
      const response = await dispatch(createManagedUser());
      const userId = _get(response, 'payload.UserId') as string | undefined;
      const mainUserId = getState().user.signedInAs.UserId;
      if (userId) {
        await dispatch(updateProfile({ ...data, UserId: userId }, true));
        dispatch(actions.createManagedAccountSuccess(userId));
        await dispatch(fetchUserProfile(false, mainUserId, 'signedInAs', true));
        return;
      }
      dispatch(actions.createManagedAccountFailed('Something went wrong'));
    } catch (error) {
      dispatch(actions.createManagedAccountFailed(_get(error, 'response.error_message')));
    }
  };
};

export default {};
